import {t} from "@lingui/core/macro";
import gql from "graphql-tag";
import * as React from "react";

import {gqlOp} from "@repo/data-gql-core";
import {doNotTranslate} from "@repo/i18n";
import {RequestInfo} from "@repo/shared-core";

import PageRoot from "#app-shell-package/page-root.tsx";
import withApolloFastlyQuery from "#data-access/gql/with-apollo-fastly-query.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedSpinner from "#shared-components-package/deprecated-spinner.tsx";

import ColumnMajorOrderVideosList from "./column-major-order-videos-list.tsx";
import AdvocateMessageLiteHomepage from "./lite-languages-advocate-message.tsx";
import {withStrings} from "./locales/shared-lite-homepage/load-strings.tsx";

import "../../stylesheets/about-package/about.less";
import "../../stylesheets/shared-package/shared.less";
import "../../stylesheets/homepage-package/homepage.less";
import "../../stylesheets/homepage-lite-package/homepage-lite.less";

import type {GqlOperation} from "@repo/data-gql-core";

import type {
    WithoutApolloFastlyQuery,
    WithSingleApolloFastlyQueryProps,
} from "#data-access/gql/with-apollo-fastly-query.tsx";

import type {
    getVideoListAndLocalesForLiteLocale,
    getVideoListAndLocalesForLiteLocaleVariables,
} from "./__graphql-types__/getVideoListAndLocalesForLiteLocale.ts";

type OwnProps = Empty;

type Props = OwnProps &
    WithSingleApolloFastlyQueryProps<getVideoListAndLocalesForLiteLocale>;

export const DATA_QUERY: GqlOperation<
    getVideoListAndLocalesForLiteLocale,
    getVideoListAndLocalesForLiteLocaleVariables
> = gqlOp(gql`
    query getVideoListAndLocalesForLiteLocale($kaLocale: String!) {
        videoListingForLocale(kaLocale: $kaLocale) {
            title
            description
            videos {
                id
                youtubeId
                title
                description
            }
        }

        localesForLanguagePicker(includeEnglish: false, includeFake: false) {
            id
            localName
            kaLocale
        }
    }
`);

export class SharedHomepageLite extends React.Component<Props> {
    renderContent(): React.ReactElement<React.ComponentProps<"div">> {
        const {
            gqlResult: {data, loading},
        } = this.props;
        const kaLocale = RequestInfo.kaLocale;

        // To build the title we want to include the locale name in the language
        // of the current locale. For example we don't want "Spanish" we want
        // "Español". So we query for the data via GraphQL and build up the
        // title based on the results that we get back.
        let title = "Khan Academy";

        if (data?.localesForLanguagePicker) {
            const curLocale = data.localesForLanguagePicker.find(
                (locale) => locale?.kaLocale === kaLocale,
            );
            const localeNameInLocalLanguage = curLocale?.localName;

            if (localeNameInLocalLanguage) {
                title = `Khan Academy ${localeNameInLocalLanguage}`;
            }
        }

        const videoLists = (data && data.videoListingForLocale) || [];

        return (
            <div>
                <article id="homepage">
                    <div>
                        <div className="alert alert-info visited-no-recolor">
                            <a
                                href="https://en.khanacademy.org/"
                                className="simple-button big-button"
                                style={{fontSize: "16px"}}
                            >
                                {doNotTranslate`View all English videos`}
                            </a>
                            <h2>{title}</h2>
                            <AdvocateMessageLiteHomepage kaLocale={kaLocale} />
                        </div>
                        <div className="clear" />
                        <div id="library">
                            {loading && <DeprecatedSpinner />}
                            {data && (
                                <div id="library-content">
                                    <div id="library-content-main">
                                        <div className="clear" />
                                        {videoLists.map((videoList, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    data-role="page"
                                                    data-theme="b"
                                                >
                                                    <div
                                                        data-role="header"
                                                        className="library-content-header"
                                                    >
                                                        <h2 className="topic-heading">
                                                            {videoList?.title}
                                                        </h2>
                                                    </div>
                                                    <div
                                                        data-role="content"
                                                        className="library-content-list"
                                                    >
                                                        <p className="topic-desc">
                                                            {
                                                                videoList?.description
                                                            }
                                                        </p>
                                                        <ColumnMajorOrderVideosList
                                                            videos={
                                                                videoList?.videos ||
                                                                []
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </article>
            </div>
        );
    }

    render(): React.ReactNode {
        return (
            <PageRoot
                settings={{
                    footer: false,
                    header: false,
                    title: t`Khan Academy`,
                }}
            >
                {this.renderContent()}
            </PageRoot>
        );
    }
}

type ExportProps = WithoutApolloFastlyQuery<
    JSX.LibraryManagedAttributes<
        typeof SharedHomepageLite,
        React.ComponentProps<typeof SharedHomepageLite>
    >,
    WithSingleApolloFastlyQueryProps<getVideoListAndLocalesForLiteLocale>
>;

export default withStrings(
    withApolloFastlyQuery(() => ({
        query: DATA_QUERY,
        variables: {kaLocale: RequestInfo.kaLocale},
    }))(SharedHomepageLite) as React.ComponentType<ExportProps>,
);
